import { useApolloClient } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useMinimizedDialogs } from "providers/minimizedDialogs";
import { useCallback, useState } from "react";

import { refetchShipmentDocumentsLists } from "./useBillOfLadingRowRefresh";

/**
 * Bail out of an in-progress upload. There is no shipping document to detach
 * yet — nothing has been applied — so discard is just deleting the uploaded
 * document, unlike the manifest flow's detach-then-delete.
 */
export function useBillOfLadingDiscard({
  onDiscarded,
  cancelRowWatch,
  initialDocumentId = null,
}: {
  onDiscarded: () => void;
  cancelRowWatch: () => void;
  /** For a document uploaded earlier, so no upload flow has to report it. */
  initialDocumentId?: string | null;
}) {
  const alerts = useAlerts();
  const { close } = useMinimizedDialogs();
  const client = useApolloClient();
  const [deleteDocument] = useDeleteDocumentMutation();

  const [uploadedDocumentId, setUploadedDocumentId] = useState<string | null>(initialDocumentId);
  const [analyzingDialogId, setAnalyzingDialogId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  const discard = useCallback(async () => {
    if (!uploadedDocumentId) {
      setConfirmOpen(false);
      return;
    }
    setDiscarding(true);
    try {
      await deleteDocument({ variables: { id: uploadedDocumentId, retainAsset: false } });
      cancelRowWatch();
      // Closed before any refetch so the dialog never renders the deleted
      // job. Its onDismiss tears down the upload flow.
      if (analyzingDialogId) close(analyzingDialogId);
      await refetchShipmentDocumentsLists(client);
      alerts.success("Upload discarded.");
      setConfirmOpen(false);
      if (!analyzingDialogId) onDiscarded();
    } catch (err) {
      alerts.error("Failed to discard the upload.", err instanceof Error ? err : undefined);
    } finally {
      setDiscarding(false);
    }
  }, [
    uploadedDocumentId,
    analyzingDialogId,
    deleteDocument,
    cancelRowWatch,
    close,
    client,
    alerts,
    onDiscarded,
  ]);

  return {
    setUploadedDocumentId,
    setAnalyzingDialogId,
    confirmOpen,
    requestConfirm: () => setConfirmOpen(true),
    cancelConfirm: () => setConfirmOpen(false),
    discarding,
    discard,
  };
}
